import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sparkles, CalendarClock, GitCompareArrows, Code2, BarChart3, CreditCard, KeyRound, BookOpen, ShieldCheck, Settings, LogOut, Coins } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LOGO_URL } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { trackVisit } from "@/lib/track";

const NAV = [
  { to: "/app", label: "Studio", icon: Sparkles, end: true, testid: "nav-studio" },
  { to: "/app/auto", label: "Auto-Studio", icon: CalendarClock, testid: "nav-auto" },
  { to: "/app/comparateur", label: "Comparateur", icon: GitCompareArrows, testid: "nav-compare" },
  { to: "/app/code", label: "CRÉRÉA Code", icon: Code2, testid: "nav-code" },
  { to: "/app/apercu", label: "Aperçu", icon: BarChart3, testid: "nav-overview" },
  { to: "/app/credits", label: "Crédits", icon: CreditCard, testid: "nav-credits" },
  { to: "/app/developpeurs", label: "Développeurs", icon: KeyRound, testid: "nav-dev" },
  { to: "/app/docs", label: "Docs API", icon: BookOpen, testid: "nav-docs" },
];

export default function DashboardLayout() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [credits, setCredits] = useState(user?.credits ?? 0);

  useEffect(() => { trackVisit(location.pathname); }, [location.pathname]);

  useEffect(() => {
    const load = () => api.get("/credits/balance").then((r) => setCredits(r.data.credits)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };
  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-gold-500/15 text-gold-400" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`;

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-border bg-card lg:flex">
        <div className="flex items-center gap-2.5 border-b border-border p-4">
          <img src={LOGO_URL} alt="CRÉRÉA" className="h-9 w-9 rounded-lg object-contain ring-1 ring-gold-500/30" />
          <div className="leading-tight"><div className="font-display text-sm font-bold tracking-tight">CRÉRÉA</div>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gold-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />AI Gateway</div></div>
        </div>
        <div className="border-b border-border p-4">
          <div className="flex items-center justify-between rounded-xl border border-gold-500/30 bg-gold-500/10 px-3 py-2">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Coins className="h-3.5 w-3.5 text-gold-400" />Crédits</span>
            <span className="font-mono font-bold text-gold-400" data-testid="credits-indicator">{credits.toLocaleString("fr-FR")}</span>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} className={linkCls}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <NavLink to="/app/admin" data-testid="nav-admin" className={linkCls}><ShieldCheck className="h-4 w-4" /> Admin · Analytics</NavLink>
              <NavLink to="/app/parametres" data-testid="nav-settings" className={linkCls}><Settings className="h-4 w-4" /> Paramètres</NavLink>
            </>
          )}
        </nav>
        <div className="border-t border-border p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="min-w-0"><p className="truncate text-sm font-medium">{user?.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p></div>
            <ThemeToggle />
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"><LogOut className="h-4 w-4" /> Déconnexion</button>
        </div>
      </aside>

      <div className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-border bg-card p-3 lg:hidden">
        <div className="flex items-center gap-2"><img src={LOGO_URL} alt="CRÉRÉA" className="h-8 w-8 rounded-lg object-contain" /><span className="font-display font-bold">CRÉRÉA</span></div>
        <span className="flex items-center gap-1 font-mono text-sm text-gold-400"><Coins className="h-4 w-4" />{credits.toLocaleString("fr-FR")}</span>
      </div>

      <main className="flex-1 pb-20 pt-16 lg:ml-60 lg:pb-0 lg:pt-0">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8"><Outlet context={{ refreshCredits: () => api.get("/credits/balance").then((r) => setCredits(r.data.credits)).catch(() => {}) }} /></div>
        <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-border bg-card py-2 lg:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-gold-400" : "text-muted-foreground"}`}>
              <n.icon className="h-5 w-5" />
            </NavLink>
          ))}
        </div>
      </main>
    </div>
  );
}
